import { observable } from '@legendapp/state';
import { logger } from '@/lib/utils/logger';
import { metricsService } from '@/lib/services/metrics.service';
import { Session } from 'next-auth';
import {
  ChartDataPoint,
  PieChartDataPoint,
} from '@/lib/services/admin.service';

// Type definitions for the metrics store
export interface MetricsState {
  userActivity: {
    data: ChartDataPoint[]; // Legacy activity event counts
    dau: ChartDataPoint[]; // Daily Active Users (distinct)
    mau: ChartDataPoint[]; // Monthly Active Users (distinct, rolling 30d)
    averageSessionDuration: number | null; // Placeholder for session duration
    isLoading: boolean;
    error: Error | null;
  };
  timeDistribution: {
    hourly: ChartDataPoint[];
    daily: ChartDataPoint[];
    weekly: ChartDataPoint[];
    isLoading: boolean;
    error: Error | null;
  };
  retention: {
    data: ChartDataPoint[];
    isLoading: boolean;
    error: Error | null;
  };
  topics: {
    totalQuestions: number | null;
    averageQuestionsPerUser: number | null;
    topics: Array<{ name: string; count: number }>;
    isLoading: boolean;
    error: Error | null;
  };
  feedback: {
    totalFeedback: number | null;
    positiveCount: number | null;
    negativeCount: number | null;
    feedbackRate: number | null;
    topReasons: Array<{ name: string; count: number }>;
    isLoading: boolean;
    error: Error | null;
  };
  interactions: {
    totalInteractions: number | null;
    averageInteractionsPerUser: number | null;
    interactionsByType: Array<{ name: string; count: number }>;
    isLoading: boolean;
    error: Error | null;
  };
  deviceUsage: {
    data: PieChartDataPoint[];
    isLoading: boolean;
    error: Error | null;
  };
  browserDistribution: {
    data: PieChartDataPoint[];
    isLoading: boolean;
    error: Error | null;
  };
  responseTimes: {
    averageLambdaResponseTime: number | null;
    averageFrontendResponseTime: number | null;
    medianLambdaResponseTime: number | null;
    p95LambdaResponseTime: number | null;
    medianFrontendResponseTime: number | null;
    p95FrontendResponseTime: number | null;
    isLoading: boolean;
    error: Error | null;
  };
  conversations: {
    totalConversations: number | null;
    averageMessagesPerConversation: number | null;
    averageDuration: number | null;
    totalMessages: number | null;
    messageCountDistribution: Array<{ range: string; count: number }>;
    isLoading: boolean;
    error: Error | null;
  };
  dateRange: {
    range: 'all' | 'year' | 'month' | 'week' | 'day';
    startDate: Date;
    endDate: Date;
  };
  sourceClicks: {
    totalSources: number | null;
    clickedSources: number | null;
    clickedPercentage: number | null;
    perType: Array<{
      type: string;
      total: number;
      clicked: number;
      percentage: number;
    }>;
    avgClicksPerConversation: number | null;
    isLoading: boolean;
    error: Error | null;
  };
  pageViews: {
    total: number;
    perPage: Array<{ page: string | null; count: number }>;
    perGroup: Array<{ group: string | null; count: number }>;
    perUser: Array<{ userId: string | null; count: number }>;
    perSession: Array<{ sessionId: string | null; count: number }>;
    timeSeries: Array<{ date: string; count: number }>;
    isLoading: boolean;
    error: Error | null;
  };
  followUps: {
    totalGenerated: number | null;
    totalClicked: number | null;
    clickRate: number | null;
    perCategory: Array<{
      category: string;
      generated: number;
      clicked: number;
      rate: number;
    }>;
    perMethod: Array<{
      method: string;
      generated: number;
      clicked: number;
      rate: number;
    }>;
    timeSeries: Array<{
      date: string;
      generated: number;
      clicked: number;
    }>;
    followUpsList: Array<{
      adviceItemId: string;
      originalQuestion: string;
      createdAt: string;
      followUps: Array<{
        id: string;
        question: string;
        category: string;
        order: number;
        isClicked: boolean;
        clickedAt: string | null;
        generationMethod: string;
        relevanceScore: number;
      }>;
    }>;
    topPerformers: Array<{
      id: string;
      question: string;
      category: string;
      generationMethod: string;
      relevanceScore: number;
      clickedAt: string | null;
    }>;
    avgRelevanceScore: number | null;
    aiGenerated: number | null;
    ruleBased: number | null;
    isLoading: boolean;
    error: Error | null;
  };
  isInitialized: boolean;
  session: Session | null;
}

// Initial state
const initialState: MetricsState = {
  userActivity: {
    data: [],
    dau: [],
    mau: [],
    averageSessionDuration: null,
    isLoading: false,
    error: null,
  },
  timeDistribution: {
    hourly: [],
    daily: [],
    weekly: [],
    isLoading: false,
    error: null,
  },
  retention: {
    data: [],
    isLoading: false,
    error: null,
  },
  topics: {
    totalQuestions: null,
    averageQuestionsPerUser: null,
    topics: [],
    isLoading: false,
    error: null,
  },
  feedback: {
    totalFeedback: null,
    positiveCount: null,
    negativeCount: null,
    feedbackRate: null,
    topReasons: [],
    isLoading: false,
    error: null,
  },
  interactions: {
    totalInteractions: null,
    averageInteractionsPerUser: null,
    interactionsByType: [],
    isLoading: false,
    error: null,
  },
  deviceUsage: {
    data: [],
    isLoading: false,
    error: null,
  },
  browserDistribution: {
    data: [],
    isLoading: false,
    error: null,
  },
  responseTimes: {
    averageLambdaResponseTime: null,
    averageFrontendResponseTime: null,
    medianLambdaResponseTime: null,
    p95LambdaResponseTime: null,
    medianFrontendResponseTime: null,
    p95FrontendResponseTime: null,
    isLoading: false,
    error: null,
  },
  conversations: {
    totalConversations: null,
    averageMessagesPerConversation: null,
    averageDuration: null,
    totalMessages: null,
    messageCountDistribution: [],
    isLoading: false,
    error: null,
  },
  dateRange: {
    range: 'month', // Default to month view
    startDate: new Date(new Date().setDate(new Date().getDate() - 30)), // 30 days ago
    endDate: new Date(), // Today
  },
  sourceClicks: {
    totalSources: null,
    clickedSources: null,
    clickedPercentage: null,
    perType: [],
    avgClicksPerConversation: null,
    isLoading: false,
    error: null,
  },
  pageViews: {
    total: 0,
    perPage: [],
    perGroup: [],
    perUser: [],
    perSession: [],
    timeSeries: [],
    isLoading: false,
    error: null,
  },
  followUps: {
    totalGenerated: null,
    totalClicked: null,
    clickRate: null,
    perCategory: [],
    perMethod: [],
    timeSeries: [],
    followUpsList: [],
    topPerformers: [],
    avgRelevanceScore: null,
    aiGenerated: null,
    ruleBased: null,
    isLoading: false,
    error: null,
  },

  isInitialized: false,
  session: null,
};

// Create the observable store
export const metricsState = observable<MetricsState>(initialState);

// Helper functions for date range calculations
function getDateRangeFromType(
  range: 'all' | 'year' | 'month' | 'week' | 'day'
): { startDate: Date; endDate: Date } {
  const now = new Date();
  const endDate = new Date(now); // Create a copy to avoid mutation issues
  let startDate: Date;

  switch (range) {
    case 'all':
      startDate = new Date(2020, 0, 1); // Start of app history
      break;
    case 'year':
      startDate = new Date(now.getFullYear(), 0, 1); // Start of current year
      break;
    case 'month':
      startDate = new Date(now.getFullYear(), now.getMonth(), 1); // Start of current month
      break;
    case 'week':
      startDate = new Date(now);
      startDate.setDate(now.getDate() - now.getDay()); // Start of current week (Sunday)
      startDate.setHours(0, 0, 0, 0);
      break;
    case 'day':
      startDate = new Date(now);
      startDate.setHours(0, 0, 0, 0); // Start of today
      break;
    default:
      startDate = new Date();
      startDate.setDate(now.getDate() - 30); // Default to last 30 days
  }

  return { startDate, endDate };
}

// Action creators
export const metricsActions = {
  // Session management
  setSession: (session: Session | null) => {
    logger.debug('[metricsStore] Setting session', { session: !!session });
    metricsState.session.set(session);
  },

  // Date range management
  setDateRange: (range: 'all' | 'year' | 'month' | 'week' | 'day') => {
    console.log('[metricsStore] setDateRange called with:', range);
    logger.debug('[metricsStore] Setting date range', { range });
    const { startDate, endDate } = getDateRangeFromType(range);

    console.log('[metricsStore] Calculated date range:', {
      range,
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
    });

    metricsState.dateRange.range.set(range);
    metricsState.dateRange.startDate.set(startDate);
    metricsState.dateRange.endDate.set(endDate);

    // Refresh data with new date range
    const isInitialized = metricsState.isInitialized.get();
    console.log('[metricsStore] isInitialized:', isInitialized);
    if (isInitialized) {
      console.log('[metricsStore] Calling fetchAllMetrics');
      metricsActions.fetchAllMetrics();
    }
  },

  // Initialize the store
  initialize: (session: Session | null) => {
    logger.debug('[metricsStore] Initializing metrics store');
    metricsActions.setSession(session);
    if (session) {
      metricsActions.fetchAllMetrics();
    }
    metricsState.isInitialized.set(true);
  },

  // Fetch all metrics
  fetchAllMetrics: async () => {
    const session = metricsState.session.get();
    if (!session) {
      logger.warn('[metricsStore] Cannot fetch metrics without a session');
      return;
    }

    // Create copies of dates to avoid mutation issues
    const dateRange = {
      startDate: new Date(metricsState.dateRange.startDate.get()),
      endDate: new Date(metricsState.dateRange.endDate.get()),
    };

    // Start loading for all metrics
    metricsState.userActivity.isLoading.set(true);
    metricsState.timeDistribution.isLoading.set(true);
    metricsState.retention.isLoading.set(true);
    metricsState.topics.isLoading.set(true);
    metricsState.feedback.isLoading.set(true);
    metricsState.interactions.isLoading.set(true);
    metricsState.deviceUsage.isLoading.set(true);
    metricsState.browserDistribution.isLoading.set(true);
    metricsState.responseTimes.isLoading.set(true);
    metricsState.conversations.isLoading.set(true);
    metricsState.pageViews.isLoading.set(true);
    metricsState.followUps.isLoading.set(true);

    try {
      // Fetch user activity metrics
      await metricsActions.fetchUserActivityMetrics();

      // Fetch time distribution metrics
      await metricsActions.fetchTimeDistributionMetrics();

      // Fetch retention metrics
      await metricsActions.fetchRetentionMetrics();

      // Fetch topic metrics
      await metricsActions.fetchTopicMetrics();

      // Fetch feedback metrics
      await metricsActions.fetchFeedbackMetrics();

      // Fetch interaction metrics
      await metricsActions.fetchInteractionMetrics();

      // Fetch device usage metrics
      await metricsActions.fetchDeviceUsageMetrics();

      // Fetch browser distribution metrics
      await metricsActions.fetchBrowserDistributionMetrics();

      // Fetch response time metrics
      await metricsActions.fetchResponseTimeMetrics();

      // Fetch conversation metrics
      await metricsActions.fetchConversationMetrics();

      await metricsActions.fetchSourceClickMetrics();

      await metricsActions.fetchPageViewMetrics();

      await metricsActions.fetchFollowUpMetrics();

      logger.debug('[metricsStore] Successfully fetched all metrics');
    } catch (error) {
      logger.error('[metricsStore] Error fetching metrics', { error });
    }
  },

  // User activity metrics
  fetchUserActivityMetrics: async () => {
    console.log('[fetchUserActivityMetrics]');
    const session = metricsState.session.get();
    // Create copies of dates to avoid mutation issues
    const dateRange = {
      startDate: new Date(metricsState.dateRange.startDate.get()),
      endDate: new Date(metricsState.dateRange.endDate.get()),
    };

    if (!session) return;

    try {
      metricsState.userActivity.isLoading.set(true);
      metricsState.userActivity.error.set(null);

      // Get user activity metrics using the consolidated service method
      const allMetrics = await metricsService.getAllMetrics(dateRange, session);
      console.log('allMetrics', allMetrics);
      const userActivityData = allMetrics.userActivity;

      if (userActivityData) {
        // Set the new DAU/MAU/Session data
        metricsState.userActivity.dau.set(userActivityData.dau || []);
        metricsState.userActivity.mau.set(userActivityData.mau || []);
        metricsState.userActivity.averageSessionDuration.set(
          userActivityData.averageSessionDuration ?? null
        );
        metricsState.userActivity.data.set(
          userActivityData.legacyActivity || []
        ); // Store legacy data if needed
      } else {
        // Reset if no data
        metricsState.userActivity.dau.set([]);
        metricsState.userActivity.mau.set([]);
        metricsState.userActivity.averageSessionDuration.set(null);
        metricsState.userActivity.data.set([]);
      }
    } catch (error) {
      logger.error('[metricsStore] Failed to fetch user activity metrics', {
        error,
      });
      metricsState.userActivity.error.set(error as Error);
    } finally {
      metricsState.userActivity.isLoading.set(false);
    }
  },

  // Topic metrics
  fetchSourceClickMetrics: async () => {
    const session = metricsState.session.get();
    // mark as initialized + loading
    metricsState.isInitialized.set(true);
    metricsState.sourceClicks.isLoading.set(true);
    metricsState.sourceClicks.error.set(null);

    // Create copies of dates to avoid mutation issues
    const dateRange = {
      startDate: new Date(metricsState.dateRange.startDate.get()),
      endDate: new Date(metricsState.dateRange.endDate.get()),
    };

    if (!session) return;

    try {
      const sourceClicksMetrics = await metricsService.getSourceClickMetrics(
        dateRange,
        session
      );

      metricsState.sourceClicks.totalSources.set(
        sourceClicksMetrics.totalSources
      );
      metricsState.sourceClicks.clickedSources.set(
        sourceClicksMetrics.clickedSources
      );
      metricsState.sourceClicks.clickedPercentage.set(
        sourceClicksMetrics.clickedPercentage
      );
      metricsState.sourceClicks.perType.set(sourceClicksMetrics.perType);
      metricsState.sourceClicks.avgClicksPerConversation.set(
        sourceClicksMetrics.avgClicksPerConversation
      );
    } catch (err) {
      metricsState.sourceClicks.error.set(
        err instanceof Error ? err : new Error(String(err))
      );
    } finally {
      metricsState.sourceClicks.isLoading.set(false);
    }
  },

  // Time distribution metrics
  fetchTimeDistributionMetrics: async () => {
    const session = metricsState.session.get();
    // Create copies of dates to avoid mutation issues
    const dateRange = {
      startDate: new Date(metricsState.dateRange.startDate.get()),
      endDate: new Date(metricsState.dateRange.endDate.get()),
    };

    if (!session) return;

    try {
      metricsState.timeDistribution.isLoading.set(true);
      metricsState.timeDistribution.error.set(null);

      const timeDistribution = await metricsService.getTimeDistribution(
        dateRange,
        session
      );

      metricsState.timeDistribution.hourly.set(timeDistribution.hourly || []);
      metricsState.timeDistribution.daily.set(timeDistribution.daily || []);
      metricsState.timeDistribution.weekly.set(timeDistribution.weekly || []);
    } catch (error) {
      logger.error('[metricsStore] Failed to fetch time distribution metrics', {
        error,
      });
      metricsState.timeDistribution.error.set(error as Error);
    } finally {
      metricsState.timeDistribution.isLoading.set(false);
    }
  },

  // Retention metrics
  fetchRetentionMetrics: async () => {
    const session = metricsState.session.get();
    // Create copies of dates to avoid mutation issues
    const dateRange = {
      startDate: new Date(metricsState.dateRange.startDate.get()),
      endDate: new Date(metricsState.dateRange.endDate.get()),
    };

    if (!session) return;

    try {
      metricsState.retention.isLoading.set(true);
      metricsState.retention.error.set(null);

      const retentionData = await metricsService.getRetentionMetrics(
        dateRange,
        session
      );
      metricsState.retention.data.set(retentionData || []);
    } catch (error) {
      logger.error('[metricsStore] Failed to fetch retention metrics', {
        error,
      });
      metricsState.retention.error.set(error as Error);
    } finally {
      metricsState.retention.isLoading.set(false);
    }
  },

  // Topic metrics
  fetchTopicMetrics: async () => {
    const session = metricsState.session.get();
    // Create copies of dates to avoid mutation issues
    const dateRange = {
      startDate: new Date(metricsState.dateRange.startDate.get()),
      endDate: new Date(metricsState.dateRange.endDate.get()),
    };

    if (!session) return;

    try {
      metricsState.topics.isLoading.set(true);
      metricsState.topics.error.set(null);

      const topicMetrics = await metricsService.getTopicMetrics(
        dateRange,
        session
      );

      // Handle the data
      metricsState.topics.totalQuestions.set(topicMetrics.totalQuestions);
      metricsState.topics.averageQuestionsPerUser.set(
        topicMetrics.averageQuestionsPerUser
      );
      metricsState.topics.topics.set(topicMetrics.topics || []);
    } catch (error) {
      logger.error('[metricsStore] Failed to fetch topic metrics', { error });
      metricsState.topics.error.set(error as Error);
    } finally {
      metricsState.topics.isLoading.set(false);
    }
  },

  fetchPageViewMetrics: async () => {
    const session = metricsState.session.get();
    // Create copies of dates to avoid mutation issues
    const dateRange = {
      startDate: new Date(metricsState.dateRange.startDate.get()),
      endDate: new Date(metricsState.dateRange.endDate.get()),
    };

    if (!session) return;

    try {
      metricsState.pageViews.isLoading.set(true);
      metricsState.pageViews.error.set(null);

      const pageViewMetrics = await metricsService.getPageViewMetrics(
        dateRange,
        session
      );

      const { total, perPage, perUser, perSession, perGroup, timeSeries } =
        pageViewMetrics || {};

      // total
      metricsState.pageViews.total.set(typeof total === 'number' ? total : 0);

      // arrays
      metricsState.pageViews.perPage.set(perPage ?? []);
      metricsState.pageViews.perUser.set(perUser ?? []);
      metricsState.pageViews.perGroup.set(perGroup ?? []);
      metricsState.pageViews.perSession.set(perSession ?? []);

      // FIXED: convert { x, y } to { date, count }
      metricsState.pageViews.timeSeries.set(
        (timeSeries ?? []).map((pt: any) => ({
          date: pt.date ?? pt.x ?? null,
          count: pt.count ?? pt.y ?? 0,
        }))
      );
    } catch (error) {
      logger.error('[metricsStore] Failed to fetch page view metrics', {
        error,
      });
      metricsState.pageViews.error.set(error as Error);
    } finally {
      metricsState.pageViews.isLoading.set(false);
    }
  },

  // Feedback metrics
  fetchFeedbackMetrics: async () => {
    const session = metricsState.session.get();
    // Create copies of dates to avoid mutation issues
    const dateRange = {
      startDate: new Date(metricsState.dateRange.startDate.get()),
      endDate: new Date(metricsState.dateRange.endDate.get()),
    };

    if (!session) return;

    try {
      metricsState.feedback.isLoading.set(true);
      metricsState.feedback.error.set(null);

      const feedbackMetrics = await metricsService.getFeedbackMetrics(
        dateRange,
        session
      );

      // Handle the data
      metricsState.feedback.totalFeedback.set(feedbackMetrics.total);
      metricsState.feedback.positiveCount.set(feedbackMetrics.positive);
      metricsState.feedback.negativeCount.set(feedbackMetrics.negative);
      metricsState.feedback.feedbackRate.set(feedbackMetrics.feedbackRate);
      metricsState.feedback.topReasons.set(feedbackMetrics.topReasons || []);
    } catch (error) {
      logger.error('[metricsStore] Failed to fetch feedback metrics', {
        error,
      });
      metricsState.feedback.error.set(error as Error);
    } finally {
      metricsState.feedback.isLoading.set(false);
    }
  },

  // Interaction metrics
  fetchInteractionMetrics: async () => {
    const session = metricsState.session.get();
    // Create copies of dates to avoid mutation issues
    const dateRange = {
      startDate: new Date(metricsState.dateRange.startDate.get()),
      endDate: new Date(metricsState.dateRange.endDate.get()),
    };

    if (!session) return;

    try {
      metricsState.interactions.isLoading.set(true);
      metricsState.interactions.error.set(null);

      const interactionMetrics = await metricsService.getInteractionMetrics(
        dateRange,
        session
      );

      // Handle different API response formats
      if (Array.isArray(interactionMetrics)) {
        // If it's an array, process it differently
        const totalInteractions = interactionMetrics.reduce(
          (sum, item) => sum + (item.count || 0),
          0
        );
        const avgPerUser =
          interactionMetrics.length > 0
            ? totalInteractions / interactionMetrics.length
            : 0;

        metricsState.interactions.totalInteractions.set(totalInteractions);
        metricsState.interactions.averageInteractionsPerUser.set(avgPerUser);
        metricsState.interactions.interactionsByType.set(
          interactionMetrics.map((item) => ({
            name: item.name || item.interaction || '',
            count: item.count || 0,
          }))
        );
      } else {
        // Regular object format
        metricsState.interactions.totalInteractions.set(
          interactionMetrics.totalInteractions
        );
        metricsState.interactions.averageInteractionsPerUser.set(
          interactionMetrics.averageInteractionsPerUser
        );
        metricsState.interactions.interactionsByType.set(
          interactionMetrics.interactionsByType || []
        );
      }
    } catch (error) {
      logger.error('[metricsStore] Failed to fetch interaction metrics', {
        error,
      });
      metricsState.interactions.error.set(error as Error);
    } finally {
      metricsState.interactions.isLoading.set(false);
    }
  },

  // Device usage metrics
  fetchDeviceUsageMetrics: async () => {
    const session = metricsState.session.get();
    // Create copies of dates to avoid mutation issues
    const dateRange = {
      startDate: new Date(metricsState.dateRange.startDate.get()),
      endDate: new Date(metricsState.dateRange.endDate.get()),
    };

    if (!session) return;

    try {
      metricsState.deviceUsage.isLoading.set(true);
      metricsState.deviceUsage.error.set(null);

      const deviceUsageData = await metricsService.getDeviceUsage(
        dateRange,
        session
      );
      metricsState.deviceUsage.data.set(deviceUsageData || []);
    } catch (error) {
      logger.error('[metricsStore] Failed to fetch device usage metrics', {
        error,
      });
      metricsState.deviceUsage.error.set(error as Error);
    } finally {
      metricsState.deviceUsage.isLoading.set(false);
    }
  },

  // Browser distribution metrics
  fetchBrowserDistributionMetrics: async () => {
    const session = metricsState.session.get();
    // Create copies of dates to avoid mutation issues
    const dateRange = {
      startDate: new Date(metricsState.dateRange.startDate.get()),
      endDate: new Date(metricsState.dateRange.endDate.get()),
    };

    if (!session) return;

    try {
      metricsState.browserDistribution.isLoading.set(true);
      metricsState.browserDistribution.error.set(null);

      const browserDistributionData =
        await metricsService.getBrowserDistribution(dateRange, session);
      metricsState.browserDistribution.data.set(browserDistributionData || []);
    } catch (error) {
      logger.error(
        '[metricsStore] Failed to fetch browser distribution metrics',
        { error }
      );
      metricsState.browserDistribution.error.set(error as Error);
    } finally {
      metricsState.browserDistribution.isLoading.set(false);
    }
  },

  // Response time metrics
  fetchResponseTimeMetrics: async () => {
    const session = metricsState.session.get();
    // Create copies of dates to avoid mutation issues
    const dateRange = {
      startDate: new Date(metricsState.dateRange.startDate.get()),
      endDate: new Date(metricsState.dateRange.endDate.get()),
    };

    if (!session) return;

    try {
      metricsState.responseTimes.isLoading.set(true);
      metricsState.responseTimes.error.set(null);

      // Use the consolidated service call
      const allMetrics = await metricsService.getAllMetrics(dateRange, session);
      const responseTimeMetrics = allMetrics.responseTimes;

      if (responseTimeMetrics) {
        // Handle the new data structure
        metricsState.responseTimes.averageLambdaResponseTime.set(
          responseTimeMetrics.averageLambdaResponseTime ?? null
        );
        metricsState.responseTimes.medianLambdaResponseTime.set(
          responseTimeMetrics.medianLambdaResponseTime ?? null
        );
        metricsState.responseTimes.p95LambdaResponseTime.set(
          responseTimeMetrics.p95LambdaResponseTime ?? null
        );
        metricsState.responseTimes.averageFrontendResponseTime.set(
          responseTimeMetrics.averageFrontendResponseTime ?? null
        );
        metricsState.responseTimes.medianFrontendResponseTime.set(
          responseTimeMetrics.medianFrontendResponseTime ?? null
        );
        metricsState.responseTimes.p95FrontendResponseTime.set(
          responseTimeMetrics.p95FrontendResponseTime ?? null
        );
      } else {
        // Reset all fields if no data
        metricsState.responseTimes.averageLambdaResponseTime.set(null);
        metricsState.responseTimes.medianLambdaResponseTime.set(null);
        metricsState.responseTimes.p95LambdaResponseTime.set(null);
        metricsState.responseTimes.averageFrontendResponseTime.set(null);
        metricsState.responseTimes.medianFrontendResponseTime.set(null);
        metricsState.responseTimes.p95FrontendResponseTime.set(null);
      }
    } catch (error) {
      logger.error('[metricsStore] Failed to fetch response time metrics', {
        error,
      });
      metricsState.responseTimes.error.set(error as Error);
    } finally {
      metricsState.responseTimes.isLoading.set(false);
    }
  },

  // Conversation metrics
  fetchConversationMetrics: async () => {
    const session = metricsState.session.get();
    // Create copies of dates to avoid mutation issues
    const dateRange = {
      startDate: new Date(metricsState.dateRange.startDate.get()),
      endDate: new Date(metricsState.dateRange.endDate.get()),
    };

    if (!session) return;

    try {
      metricsState.conversations.isLoading.set(true);
      metricsState.conversations.error.set(null);

      // Fetch data using the consolidated getAllMetrics which now returns the correct structure
      const allMetrics = await metricsService.getAllMetrics(dateRange, session);
      const conversationMetrics = allMetrics.conversationLength;

      if (conversationMetrics) {
        // Format distribution data first to calculate total conversations
        const formattedDistribution = (conversationMetrics.distribution || [])
          .filter(
            (item): item is ChartDataPoint & { x: string | number } =>
              item &&
              item.x !== undefined &&
              item.y !== undefined &&
              Number(item.y) > 0
          )
          .map((item) => ({
            range: `${item.x} messages`,
            count: Number(item.y),
          }))
          .sort(
            (a, b) =>
              Number(a.range.split(' ')[0]) - Number(b.range.split(' ')[0])
          );

        // Calculate total conversations from the distribution
        const calculatedTotalConversations =
          formattedDistribution.length > 0
            ? formattedDistribution.reduce((sum, item) => sum + item.count, 0)
            : null;

        // Set state using backend-calculated values and derived total
        metricsState.conversations.totalConversations.set(
          calculatedTotalConversations
        );
        metricsState.conversations.totalMessages.set(
          conversationMetrics.totalMessages ?? null
        );
        metricsState.conversations.averageMessagesPerConversation.set(
          conversationMetrics.averageLength ?? null // Backend names this averageLength
        );
        metricsState.conversations.averageDuration.set(
          conversationMetrics.averageDuration ?? null
        );
        metricsState.conversations.messageCountDistribution.set(
          formattedDistribution // Use the formatted data
        );
      } else {
        // Reset values if no valid data
        metricsState.conversations.totalConversations.set(null);
        metricsState.conversations.totalMessages.set(null);
        metricsState.conversations.averageMessagesPerConversation.set(null);
        metricsState.conversations.averageDuration.set(null);
        metricsState.conversations.messageCountDistribution.set([]);
      }
    } catch (error) {
      logger.error('[metricsStore] Failed to fetch conversation metrics', {
        error,
      });
      metricsState.conversations.error.set(error as Error);
    } finally {
      metricsState.conversations.isLoading.set(false);
    }
  },

  // Follow-up metrics
  fetchFollowUpMetrics: async () => {
    const session = metricsState.session.get();
    // Create copies of dates to avoid mutation issues
    const dateRange = {
      startDate: new Date(metricsState.dateRange.startDate.get()),
      endDate: new Date(metricsState.dateRange.endDate.get()),
    };

    console.log('[fetchFollowUpMetrics] Starting fetch with dateRange:', {
      startDate: dateRange.startDate?.toISOString(),
      endDate: dateRange.endDate?.toISOString(),
      hasSession: !!session,
    });

    if (!session) {
      console.log('[fetchFollowUpMetrics] No session, returning');
      return;
    }

    try {
      metricsState.followUps.isLoading.set(true);
      metricsState.followUps.error.set(null);

      const followUpMetrics = await metricsService.getFollowUpMetrics(
        dateRange,
        session
      );

      console.log('[fetchFollowUpMetrics] Received data:', {
        totalGenerated: followUpMetrics?.totalGenerated,
        totalClicked: followUpMetrics?.totalClicked,
        followUpsListLength: followUpMetrics?.followUpsList?.length,
        hasData: !!followUpMetrics,
      });

      if (followUpMetrics) {
        metricsState.followUps.totalGenerated.set(
          followUpMetrics.totalGenerated ?? null
        );
        metricsState.followUps.totalClicked.set(
          followUpMetrics.totalClicked ?? null
        );
        metricsState.followUps.clickRate.set(
          followUpMetrics.clickRate ?? null
        );
        metricsState.followUps.perCategory.set(
          followUpMetrics.perCategory ?? []
        );
        metricsState.followUps.perMethod.set(followUpMetrics.perMethod ?? []);
        metricsState.followUps.timeSeries.set(
          followUpMetrics.timeSeries ?? []
        );
        metricsState.followUps.followUpsList.set(
          followUpMetrics.followUpsList ?? []
        );
        metricsState.followUps.topPerformers.set(
          followUpMetrics.topPerformers ?? []
        );
        metricsState.followUps.avgRelevanceScore.set(
          followUpMetrics.avgRelevanceScore ?? null
        );
        metricsState.followUps.aiGenerated.set(
          followUpMetrics.aiGenerated ?? null
        );
        metricsState.followUps.ruleBased.set(
          followUpMetrics.ruleBased ?? null
        );
      } else {
        // Reset values if no valid data
        metricsState.followUps.totalGenerated.set(null);
        metricsState.followUps.totalClicked.set(null);
        metricsState.followUps.clickRate.set(null);
        metricsState.followUps.perCategory.set([]);
        metricsState.followUps.perMethod.set([]);
        metricsState.followUps.timeSeries.set([]);
        metricsState.followUps.followUpsList.set([]);
        metricsState.followUps.topPerformers.set([]);
        metricsState.followUps.avgRelevanceScore.set(null);
        metricsState.followUps.aiGenerated.set(null);
        metricsState.followUps.ruleBased.set(null);
      }
    } catch (error) {
      logger.error('[metricsStore] Failed to fetch follow-up metrics', {
        error,
      });
      metricsState.followUps.error.set(error as Error);
    } finally {
      metricsState.followUps.isLoading.set(false);
    }
  },
};

export default metricsActions;
